import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JournalEntryTable from './components/JournalEntryTable';
import JournalEntryModal from './components/JournalEntryModal';
import JournalEntryFilters from './components/JournalEntryFilters';
import EntryTemplateModal from './components/EntryTemplateModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { getJournalEntries, createJournalEntry, deleteJournalEntry } from '../../services/journalService';
import { formatCurrency } from '../../utils/currency';

const JournalEntries = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [sortConfig, setSortConfig] = useState({ column: 'entryDate', direction: 'desc' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadJournalEntries();
  }, []);

  const loadJournalEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Loading journal entries...');
      const data = await getJournalEntries();
      console.log('📊 Journal entries data received:', data);
      console.log('📊 Data type:', typeof data, 'Is array:', Array.isArray(data));
      console.log('📊 Data length:', data?.length);
      if (data && data.length > 0) {
        console.log('📊 First entry:', data[0]);
      }
      setJournalEntries(data);
    } catch (err) {
      console.error('❌ Error loading journal entries:', err);
      setError('Failed to load journal entries. Please try again.');
      setJournalEntries([]); // Ensure we set an empty array on error
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = useMemo(() => {
    // Ensure journalEntries is always an array
    const entriesArray = Array.isArray(journalEntries) ? journalEntries : [];
    console.log('Filtering entries:', entriesArray, 'Length:', entriesArray.length);
    
    let filtered = [...entriesArray];

    if (searchTerm) {
      filtered = filtered?.filter(entry =>
        entry?.entryNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        entry?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        entry?.reference?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(entry => entry?.status === statusFilter);
    }

    if (dateRange !== 'all') {
      const today = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          filtered = filtered?.filter(entry => new Date(entry?.entryDate) >= filterDate);
          break;
        case 'week':
          filterDate?.setDate(today?.getDate() - 7);
          filtered = filtered?.filter(entry => new Date(entry?.entryDate) >= filterDate);
          break;
        case 'month':
          filterDate?.setMonth(today?.getMonth() - 1);
          filtered = filtered?.filter(entry => new Date(entry?.entryDate) >= filterDate);
          break;
        case 'quarter':
          filterDate?.setMonth(today?.getMonth() - 3);
          filtered = filtered?.filter(entry => new Date(entry?.entryDate) >= filterDate);
          break;
        default:
          break;
      }
    }

    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.column];
      const bValue = b?.[sortConfig?.column];
      
      if (sortConfig?.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [journalEntries, searchTerm, statusFilter, dateRange, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectEntry = (entryId) => {
    setSelectedEntries(prev =>
      prev?.includes(entryId)
        ? prev?.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEntries?.length === filteredEntries?.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filteredEntries?.map(entry => entry?.id));
    }
  };

  const handleCreateEntry = async (entryData) => {
    try {
      console.log('Creating journal entry:', entryData);
      setLoading(true);
      
      await createJournalEntry(entryData);
      
      // Reload the journal entries to show the new entry
      await loadJournalEntries();
      
      // Close the modal
      setIsCreateModalOpen(false);
      setSelectedEntry(null);
      
      console.log('Journal entry created successfully');
    } catch (error) {
      console.error('Error creating journal entry:', error);
      setError('Failed to create journal entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setIsCreateModalOpen(true);
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      console.log('🗑️ Deleting journal entry:', entryId);
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Call the delete API
      await deleteJournalEntry(entryId);
      
      // Reload the journal entries to reflect the deletion
      await loadJournalEntries();
      
      console.log('✅ Journal entry deleted successfully');
      
      // Clear selection if the deleted entry was selected
      setSelectedEntries(prev => prev.filter(id => id !== entryId));
      
      // Show success message (you can enhance this with a proper toast notification)
      console.log('📝 Journal entry deleted successfully');
      
    } catch (error) {
      console.error('❌ Error deleting journal entry:', error);
      setError(error.message || 'Failed to delete journal entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setIsDeleteModalOpen(true);
  };

  const handleReverseEntry = (entryId) => {
    console.log('Reversing entry:', entryId);
  };

  const handleToggleExpand = (entryId) => {
    setExpandedEntries(prev =>
      prev?.includes(entryId)
        ? prev?.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleApplyTemplate = (template) => {
    console.log('Applying template:', template);
    setIsTemplateModalOpen(false);
    setIsCreateModalOpen(true);
  };

  const quickActions = [
    {
      label: 'New Entry',
      icon: 'Plus',
      onClick: () => {
        setSelectedEntry(null);
        setIsCreateModalOpen(true);
      },
      variant: 'default'
    },
    {
      label: 'Templates',
      icon: 'FileText',
      onClick: () => setIsTemplateModalOpen(true),
      variant: 'outline'
    },
    {
      label: 'Export',
      icon: 'Download',
      onClick: () => console.log('Export entries'),
      variant: 'outline'
    }
  ];

  const stats = [
    { label: 'Total Entries', value: Array.isArray(journalEntries) ? journalEntries.length : 0, icon: 'FileText', color: 'primary' },
    { label: 'Posted', value: Array.isArray(journalEntries) ? journalEntries.filter(e => e?.status === 'Posted').length : 0, icon: 'CheckCircle', color: 'success' },
    { label: 'Pending', value: Array.isArray(journalEntries) ? journalEntries.filter(e => e?.status === 'Pending Approval').length : 0, icon: 'Clock', color: 'warning' },
    { label: 'Draft', value: Array.isArray(journalEntries) ? journalEntries.filter(e => e?.status === 'Draft').length : 0, icon: 'Edit', color: 'secondary' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading journal entries...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="mx-auto text-destructive mb-4" />
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Error Loading Journal Entries</h3>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={loadJournalEntries}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground">Journal Entries</h1>
              <p className="text-muted-foreground mt-2">Manage manual accounting entries and adjustments</p>
            </div>
            <QuickActionToolbar actions={quickActions} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats?.map((stat, index) => (
              <div key={index} className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat?.label}</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{stat?.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg bg-${stat?.color}/10 flex items-center justify-center`}>
                    <Icon name={stat?.icon} size={24} color={`var(--color-${stat?.color})`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <JournalEntryFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />

          <JournalEntryTable
            entries={filteredEntries}
            selectedEntries={selectedEntries}
            expandedEntries={expandedEntries}
            onSelectEntry={handleSelectEntry}
            onSelectAll={handleSelectAll}
            onToggleExpand={handleToggleExpand}
            sortConfig={sortConfig}
            onSort={handleSort}
            onEdit={handleEditEntry}
            onDelete={handleDeleteClick}
            onReverse={handleReverseEntry}
          />

          {filteredEntries?.length === 0 && !loading && (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold text-lg text-foreground mb-2">No journal entries found</h3>
              <p className="text-muted-foreground mb-6">Create your first journal entry to get started</p>
              <div className="flex items-center justify-center gap-3">
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Entry
                </Button>
                <Button variant="outline" onClick={loadJournalEntries}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <JournalEntryModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedEntry(null);
        }}
        onSubmit={handleCreateEntry}
        entry={selectedEntry}
      />

      <EntryTemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onApplyTemplate={handleApplyTemplate}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEntryToDelete(null);
        }}
        onConfirm={handleDeleteEntry}
        entry={entryToDelete}
      />
    </div>
  );
};

export default JournalEntries;